/* eslint-disable @typescript-eslint/no-explicit-any */
import {UseFormReturn} from 'react-hook-form';
import Api from 'types/api';

/**
 * This function is used to handle errors from the API.
 * By default the `Key` is set to 'nonFieldErrors' however you can choose any other key if you need to.
 *
 * Generally this function is best used when the results from Django is NOT related to a Django form.
 *
 * ```
 * mutateAsync({
 *  formData,
 *  mid,
 *  requestId: mid,
 * })
 * .then(onApiKeySuccess(methods, mid))
 * .catch(onApiKeyError(methods, mid));
 * ```
 */
export const onApiKeyError =
    (methods: UseFormReturn<any>, key = 'nonFieldErrors') =>
    (error: Api.Response.Failure) => {
        const {setError} = methods;

        setError(`root.${key}`, {
            message: error.message,
            type: 'error',
        });
    };

/**
 * This function is used to handle Django form errors from the API.
 *
 * Generally this function is best used when the results from Django is related to a Django form.
 *
 * ```
 * mutateAsync(formData)
 * .then(onApiKeySuccess(methods, mid))
 * .catch(onApiFormError(methods));
 * ```
 */
export const onApiFormError =
    (methods: UseFormReturn<any>, key = 'nonFieldErrors') =>
    (error: Api.Response.Failure) => {
        const {setError} = methods;

        if ('fieldErrors' in error) {
            error.fieldErrors.forEach(({key: fieldKey, value}) => {
                if (fieldKey === 'nonFieldErrors') {
                    setError('root.nonFieldErrors', {
                        message: value.message,
                        type: 'error',
                    });
                } else {
                    setError(fieldKey, {
                        message: value.message,
                        type: 'error',
                    });
                }
            });
        } else {
            setError(`root.${key}`, {
                message: error.message,
                type: 'error',
            });
        }
    };

/**
 * This function is used to set a success message from the API.
 * By default the `Key` is set to 'successMessage' however you can choose any other key if you need to.
 *
 * ```
 * mutateAsync(formData)
 * .then(onApiKeySuccess(methods, 'mySuccessKey'))
 * .catch(onApiFormError(methods));
 * ```
 */
export const onApiKeySuccess =
    (methods: UseFormReturn<any>, key = 'successMessage') =>
    (response: Api.Response.Success<any>) => {
        if (response) {
            const {setError} = methods;

            setError(`root.${key}`, {
                message: response.data.message,
                type: 'success',
            });
        }
    };
